/**
 * Чистый звук компьютера для трансляции.
 *
 * На Windows 10 2004+ система умеет отдать loopback «по дереву процессов»: оболочка просит
 * всё, кроме процессов Кличата, и присылает сюда PCM. Получается то, что делает Discord:
 * звук игры едет зрителям, а голоса собеседников в трансляцию не попадают — не потому что мы
 * их вычистили, а потому что их там никогда не было.
 *
 * На macOS 13+ оболочка получает такой же поток через ScreenCaptureKit.
 * Если модуля нет или запуск не удался, startSystemAudio вернёт null.
 * Windows берёт звук обычным путём с вычитанием голосов (см. streamStrip.ts);
 * на macOS без нативного захвата системного звука нет.
 */
import { desktop } from '@/lib/desktop'

export interface SystemAudioStats {
  /** Сколько звука лежит про запас, мс. */
  bufferedMs: number
  /** Сколько не хватило за всё время, мс: паузы в звуке сюда тоже попадают. */
  starvedMs: number
  /** Сколько сбросили из-за расхождения часов, мс. */
  trimmedMs: number
  /** Сколько звука сыграно всего, мс. Ноль спустя минуты трансляции — захват молчит. */
  playedMs: number
}

export interface SystemAudioCapture {
  track: MediaStreamTrack
  stop: () => Promise<void>
  onStats: ((stats: SystemAudioStats) => void) | null
}

/** Есть ли вообще чем захватывать: платформа, версия оболочки, наличие модуля. */
export async function systemAudioAvailable(): Promise<boolean> {
  const api = desktop?.systemAudio
  if (!api) return false
  return api.available().catch(() => false)
}

/**
 * Запустить захват и отдать готовую дорожку. Звук пойдёт ещё до выбора окна — это нормально,
 * дорожка публикуется отдельно и до публикации никуда не уходит.
 *
 * Возвращает null, если захват недоступен или не завёлся: трансляцию из-за звука не теряем.
 */
export async function startSystemAudio(ctx: AudioContext): Promise<SystemAudioCapture | null> {
  const api = desktop?.systemAudio
  if (!api) return null
  // спрашиваем до обработки: модуля может не быть в старой оболочке или на этой системе
  if (!(await api.available().catch(() => false))) return null

  let node: AudioWorkletNode | null = null
  let unsubscribe: (() => void) | null = null
  let dest: MediaStreamAudioDestinationNode | null = null
  const capture: SystemAudioCapture = { track: null as unknown as MediaStreamTrack, stop: async () => undefined, onStats: null }

  try {
    if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined)
    await ctx.audioWorklet.addModule(new URL('./sysWorklet.js', import.meta.url))
    node = new AudioWorkletNode(ctx, 'klichat-sysaudio', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
    })
    node.port.onmessage = (e: MessageEvent<{ type?: string; buffered?: number; starved?: number; trimmed?: number; frames?: number }>) => {
      const m = e.data
      if (m.type !== 'stats') return
      const ms = (frames: number | undefined): number => Math.round(((frames ?? 0) / ctx.sampleRate) * 1000)
      capture.onStats?.({ bufferedMs: ms(m.buffered), starvedMs: ms(m.starved), trimmedMs: ms(m.trimmed), playedMs: ms(m.frames) })
    }
    dest = ctx.createMediaStreamDestination()
    node.connect(dest)

    // слушателя ставим до start(), иначе первые пачки уедут в никуда
    const target = node
    unsubscribe = api.onData((bytes) => {
      // чётное число байт: пришёл поток 16-битных отсчётов, половинки быть не должно
      const whole = bytes.byteLength & ~1
      if (whole === 0) return
      const pcm = new Int16Array(whole >> 1)
      new Uint8Array(pcm.buffer).set(bytes.subarray(0, whole))
      target.port.postMessage(pcm, [pcm.buffer])
    })

    const started = await api.start()
    if (!started.ok) throw new Error(started.error ?? 'система не дала звук')

    const track = dest.stream.getAudioTracks()[0]
    if (!track) throw new Error('дорожка не создалась')
    // для собеседников это звук компьютера, а не микрофон: обработке не подлежит
    track.contentHint = 'music'
    capture.track = track
    capture.stop = async () => {
      unsubscribe?.()
      unsubscribe = null
      await api.stop().catch(() => undefined)
      node?.port.postMessage({ type: 'stop' })
      node?.disconnect()
      track.stop()
      node = null
      dest = null
    }
    return capture
  } catch (err) {
    unsubscribe?.()
    node?.disconnect()
    await api.stop().catch(() => undefined)
    console.warn('чистый звук системы не включился:', err instanceof Error ? err.message : err)
    return null
  }
}
